import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import {
  Recurrencia,
  RecurrenciaDataPagingRes,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';
import { urlApiRecurrencia, urlBackend } from '@/shared/constants/networking.constants';
import { NavigateFunction } from 'react-router-dom';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchRecurrencias = (page = 1, nombre = '', page_size = 10) => {
  return useQuery({
    queryKey: ['recurrencias', page, nombre],
    queryFn: async () => {
      return getRecurrencias({ page, nombre, page_size });
    },
  });
};

export const useGetRecurrencia = (id: string) => {
  return useQuery({
    queryKey: ['recurrencias', id],
    queryFn: async () => {
      return getRecurrencia(id);
    },
  });
};

export const useCreateRecurrencia = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRecurrencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurrencias'] });
      navigate(returnUrl);
    },
  });
};

export const useUpdateRecurrencia = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateRecurrencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurrencias'] });
      navigate(returnUrl);
    },
  });
};

export const useRemoveRecurrencia = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeRecurrencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurrencias'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getRecurrenciasMethodProps = {
  page?: number;
  nombre?: string;
  page_size?: number;
};

export type CreateRecurrenciaMethodProps = Omit<Recurrencia, 'id_recurrencia'>;

export const getRecurrencias = async (props?: getRecurrenciasMethodProps) => {
  const { page = 1, nombre = '', page_size = 10 } = props || {};

  return get<RecurrenciaDataPagingRes>(
    `${urlBackend}api/v1/recurrencias/?page=${page}&nombre=${nombre}&page_size=${page_size}`,
    true
  );
};

export const getRecurrencia = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<Recurrencia>(`${urlApiRecurrencia}${id}/`, true);

  res.status === 'error' &&
    setAlert(true, 'Recurrencia no encontrada', 'error');

  return res;
};

export const createRecurrencia = async (data: CreateRecurrenciaMethodProps) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Recurrencia>(urlApiRecurrencia, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateRecurrencia = async ({
  id,
  data,
}: {
  id: string;
  data: CreateRecurrenciaMethodProps;
}) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Recurrencia>(`${urlApiRecurrencia}${id}/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const removeRecurrencia = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Recurrencia>(`${urlApiRecurrencia}${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};
